
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Share2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { checkPremiumStatusSync } from "@/App";
import { shareEntryWithEmail, getEntryShares, deleteEntryShare } from "@/utils/supabase";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";

// Import the smaller components
import ShareDialogHeader from "./share/ShareDialogHeader";
import ShareFormSection from "./share/ShareFormSection";
import SharesList from "./share/SharesList";
import SharePremiumFooter from "./share/SharePremiumFooter";

interface ShareEntryDialogProps {
  entryId: string;
}

interface ShareInfo {
  id: string;
  email: string;
  access_code: string;
  created_at: string;
}

const ShareEntryDialog = ({ entryId }: ShareEntryDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shares, setShares] = useState<ShareInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isPremium = checkPremiumStatusSync();
  const navigate = useNavigate();

  const loadShares = async () => {
    if (!isOpen) return;
    
    setIsLoading(true);
    const { data, error } = await getEntryShares(entryId);
    
    if (error) {
      toast.error("Failed to load shares");
    } else if (data) {
      setShares(data as ShareInfo[]);
    }
    
    setIsLoading(false);
  };

  const handleShareEntry = async (email: string) => {
    if (!email) {
      toast.error("Please enter an email address");
      return;
    }
    
    if (!isPremium) {
      toast.error("Sharing is a premium feature");
      setIsOpen(false);
      navigate("/premium");
      return;
    }
    
    setIsSharing(true);
    toast.loading("Sharing diary entry and sending notification...");
    
    const { data, error, emailSent } = await shareEntryWithEmail(entryId, email);
    
    toast.dismiss();
    
    if (error) {
      if (error.message.includes("duplicate key") || 
          error.message.includes("already been shared")) {
        toast.info("This entry has already been shared with this email");
      } else {
        toast.error(`Failed to share diary entry: ${error.message}`);
      }
    } else {
      if (emailSent === false) {
        toast.success("Diary entry shared, but email notification failed to send", {
          description: "The recipient can still access the entry with a link"
        });
      } else {
        toast.success("Diary entry shared and notification email sent");
      }
      loadShares();
    }
    
    setIsSharing(false);
  };

  const handleCopyLink = (accessCode: string) => {
    // Construct a shareable link
    const shareUrl = `${window.location.origin}/entries/shared/${entryId}?code=${accessCode}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied to clipboard");
  };

  const handleDeleteShare = async (shareId: string) => {
    const { error } = await deleteEntryShare(shareId);
    
    if (error) {
      toast.error("Failed to remove share");
    } else {
      toast.success("Share removed successfully");
      setShares(shares.filter(share => share.id !== shareId));
    }
  };

  const handleUpgradeToPremium = () => {
    setIsOpen(false);
    navigate("/premium");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (open) {
        loadShares();
      }
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="flex items-center gap-2">
          <Share2 className="h-4 w-4" />
          <span>Share</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <ShareDialogHeader isPremium={isPremium} />
        
        {isPremium ? (
          <>
            <ShareFormSection 
              onShareEntry={handleShareEntry} 
              isSharing={isSharing} 
            />
            
            {shares.length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Shared with</h3>
                  
                  <SharesList 
                    shares={shares}
                    isLoading={isLoading}
                    onCopyLink={handleCopyLink}
                    onDeleteShare={handleDeleteShare}
                  />
                </div>
              </>
            )}
          </>
        ) : (
          <SharePremiumFooter onUpgradeToPremium={handleUpgradeToPremium} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareEntryDialog;
